import { useState } from "react";
import { Box,
	Divider,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Icon,
	Image,
	SimpleGrid,
	Stack,
	Text
} from "@chakra-ui/react";
import { FiCornerDownRight } from "react-icons/fi";
import { useHistory } from "react-router-dom";

import { useAuth } from "../../hooks/auth";
import { useCart } from "../../hooks/cart";
import { formatPrice } from "../../utils/format";
import AlertResumeOrder from "./AlertResumeOrder";

interface PedidoDrawerProps {
	isOpen: boolean;
	onRequestClose: (close: boolean) => void;
	pedido: any;
}

const PedidoDrawer: React.FC<PedidoDrawerProps> = ({
	isOpen,
	onRequestClose,
	pedido
}) => {
	const { company } = useAuth();
	const { cart, resumeCart } = useCart();
	const history = useHistory();

	const [isOpenAlertResumeOrder, setIsOpenAlertResumeOrder] = useState(false);

	function resumeCartOrder() {
		if (cart.items.length > 0) {
			setIsOpenAlertResumeOrder(true);
		} else {
			resumeCart(pedido);
			history.push('/products');
		}
	}

	return (
		<>
			<Drawer
				isOpen={isOpen}
				placement="right"
				onClose={() => onRequestClose(false)}
				size="md"
				blockScrollOnMount={false}
			>
				<DrawerOverlay />

				<DrawerContent mt={["50px", "60px"]}>
					<DrawerCloseButton />

					<DrawerHeader pt="2" pb="1">
						<Flex direction="column">
							<Text fontSize="13" fontWeight="500" color="gray.300" mb="-1">N. do pedido</Text>
							<Text fontSize="15" fontWeight="500" color="gray.600">
								{pedido.id_order} - {pedido.order_data}
							</Text>

							<Flex justify='space-between'>
								<Text fontSize="14" fontWeight="500" color="gray.400">
									{pedido.operacao?.nome}
								</Text>

								{pedido.operacao?.concluido === 'N' && (
									<Text
										as="button"
										fontSize="14"
										fontWeight="500"
										color="blue.100"
										cursor="pointer"
										onClick={resumeCartOrder}
									>Retomar</Text>
								)}
							</Flex>
						</Flex>
					</DrawerHeader>

					<Box px="4"><Divider orientation='horizontal' size="sm" /></Box>
					
					<DrawerBody>
						{/* CLIENTE */}
						<Flex direction="column" pb="1">
							<Text fontSize="13px" fontWeight="500" color="gray.300" mb="-1">
								{pedido.consumidor ? 'Consumidor' : 'Cliente'}
							</Text>
							<Text fontSize="15px" fontWeight="500" color="gray.600">
								{pedido.cliente?.nome ? pedido.cliente.nome : 'Nenhum cliente'}
							</Text>
							<Text fontSize="13px" fontWeight="500" color="gray.500">
								{pedido.cliente?.doc && pedido.cliente.doc}
							</Text>
						</Flex>

						<Box mx="-10px"><Divider orientation='horizontal' size="sm" /></Box>

						{/* LOJA */}
						<Flex direction="column" py="1">
							<Text fontSize="13px" fontWeight="500" color="gray.300" mb="-1">Loja</Text>
							<Text fontSize="15px" fontWeight="500" color="gray.600">{company.fantazia}</Text>
						</Flex>

						{/* INDICADOR */}
						{pedido.indicador?.id && (
							<Flex direction="column" pb="1">
								<Text fontSize="13px" fontWeight="500" color="gray.300" mb="-1">
									Indicador
								</Text>
								<Text fontSize="15px" fontWeight="500" color="gray.600">
									{pedido.indicador?.nome}
								</Text>
							</Flex>
						)}

						{/* VENDEDOR */}
						{pedido.vendedor?.id && (
							<>
								<Divider orientation='horizontal' size="sm" />
								<Flex direction="column">
									<Text fontSize="13px" fontWeight="500" color="gray.300" mb="-1">Vendedor</Text>
									<Text fontSize="15px" fontWeight="500" color="gray.600">{pedido.vendedor.nome}</Text>
								</Flex>
							</>
						)}

						<Box mx="-10px"><Divider orientation='horizontal' size="sm" /></Box>

						{/* ITEMS */}
						<SimpleGrid columns={1} py="1">
							<Text fontSize="13px" fontWeight="500" color="gray.300" mb="-1">Items</Text>
							{pedido.items?.map((item: any) => {
								return (
									<Flex
										key={item.id}
										flexDir="column"
										py="1"
										_notLast={{
											borderBottomWidth: 1,
											borderBottomColor: "gray.200"
										}}
									>
										<Flex justify="space-between">
											<Text fontSize="15px">{item.qtd}x {item.nome}</Text>
											<Text fontSize="15px">{formatPrice(item.total)}</Text>
										</Flex>

										{item.complementos?.map((comp: any) => {
											return comp.qtd > 0 && (
												<Flex key={comp.codigo} px="2">
													<Icon as={FiCornerDownRight} color="gray.300" />
													<Text fontSize="14px">{comp.qtd}x {comp.nome}</Text>
												</Flex>
											)
										})}
									</Flex>
								)
							})}
						</SimpleGrid>

						<Box mx="-10px"><Divider orientation='horizontal' size="sm" /></Box>

						{/* TOTAIS */}
						<Flex direction="column" py="1">
							<Flex justify="space-between">
								<Text fontSize="15" fontWeight="500">SubTotal</Text>
								<Text fontSize="15" fontWeight="500">{formatPrice(pedido.subTotal)}</Text>
							</Flex>

							<Flex justify="space-between">
								<Text fontSize="15" fontWeight="500" color="gray.400">Taxa Entrega</Text>
								<Text fontSize="15" fontWeight="500" color="gray.400">{formatPrice(pedido.taxa_entrega)}</Text>
							</Flex>

							<Flex justify="space-between">
								<Text fontSize="15" fontWeight="500" color="gray.800">Total</Text>
								<Text fontSize="15" fontWeight="500" color="gray.800">{formatPrice(pedido.total)}</Text>
							</Flex>
						</Flex>

						<Box mx="-10px"><Divider orientation='horizontal' size="sm" /></Box>

						{/* ENTREGA */}
						<Flex direction="column" py="1">
							<Text fontSize="13px" fontWeight="500" color="gray.300" mb="-1">
								{pedido.retirar ? 'Retirada em': 'Entrega em'}
							</Text>
							<Flex direction="column" fontSize="15px" fontWeight="500" color="gray.600" pl="1">
								<Text fontSize="14" fontWeight="500" color="gray.700">
									{pedido.retirar ? company.endereco.endereco : pedido.endereco_entrega?.endereco 
										? pedido.endereco_entrega?.endereco : 'Nenhum endereço'
									}
									{pedido.retirar ? `, ${company.endereco.num}` 
										: pedido.endereco_entrega?.num && `, ${pedido.endereco_entrega?.num}`
									}
								</Text>
								<Text fontSize="14" fontWeight="500" color="gray.400">
									{pedido.retirar ? `${company.endereco.comp}, `
										: pedido.endereco_entrega?.comp && `${pedido.endereco_entrega?.comp}, `
									}
									{pedido.retirar ? company.endereco.bai : pedido.endereco_entrega?.bai}
									{pedido.retirar ? `, ${company.endereco.cid}`
										: pedido.endereco_entrega?.cid && `, ${pedido.endereco_entrega?.cid}`
									}
									{pedido.retirar ? ` - ${company.endereco.uf}`
										: pedido.endereco_entrega?.uf && ` - ${pedido.endereco_entrega?.uf}`
									}
								</Text>
							</Flex>
						</Flex>

						<Box mx="-10px"><Divider orientation='horizontal' size="sm" /></Box>

						{/* PAGAMENTO */}
							<Flex direction="column" py="1">
								<Text fontSize="13px" fontWeight="500" color="gray.300" mb="-1">Pagamento</Text>
								{pedido.pagamentos?.length > 0 ? pedido.pagamentos.map((pay: any) => {
									return (
										<Flex
											key={pay.id}
											justify="space-between"
											w="100%"
											p="2"
											my="1"
											borderWidth={1}
											borderColor="gray.100"
											borderRadius={8}
										>
											<Stack direction="row" spacing="2">
												<Image w="28px" h="23px" src={pay.url_pay} alt={pay.name_pay} />
												<Text fontSize="15" fontWeight="500">{pay.name_pay}</Text>
												<Text fontSize="15" fontWeight="500">{pay.qtd_parcel}x</Text>
											</Stack>

											<Stack
												direction="row"
												spacing="2"
												justify="center"
												align="center"
											>
												<Text>{formatPrice(pay.vlr_pay)}</Text>
											</Stack>
										</Flex>
									)
								}) : 'Nenhum pagamento'}
							</Flex>
					</DrawerBody>
				</DrawerContent>
			</Drawer>

			{isOpenAlertResumeOrder && (
				<AlertResumeOrder
					isOpen={isOpenAlertResumeOrder}
					onRequestClose={setIsOpenAlertResumeOrder}
					pedido={pedido}
				/>
			)}
		</>
	)
};

export default PedidoDrawer;